import React, { useMemo, useState } from "react";
import { ToastContainer } from "react-toastify";
import * as axios from "axios";
import {
  Badge,
  Button,
  CheckCircle,
  Code02,
  CopyButton,
  DotsVertical,
  Edit05,
  EmptyState,
  EyeOff,
  FilterBar,
  Mail01,
  Menu,
  PageHeader,
  Pagination,
  Plus,
  SearchLg,
  Send01,
  Table,
  Tag,
  TextInput,
  Trash01,
  Users01,
} from "@opendate-app/setlist";

import { toastError, toastSuccess } from "../../../shared/toastHelper";
import SubmissionsDrawer from "./SubmissionsDrawer";
import buildSignupFormEmbed from "./buildSignupFormEmbed";
import FormStatusBadge from "./components/FormStatusBadge";

const PER_PAGE = 10;

const cardStyle = {
  marginTop: 24,
  background: "var(--sl-bg-primary)",
  borderRadius: "12px",
  boxShadow: "var(--sl-shadow-xs)",
  border: "1px solid var(--sl-border-secondary)",
};

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Draft" },
];

const EMPTY_FEATURES = [
  { icon: Code02, title: "Embed anywhere", text: "Copy one snippet onto any site — no code required." },
  { icon: Users01, title: "Grow your audience", text: "Choose the fields you collect — email, phone, and name." },
  { icon: CheckCircle, title: "Auto-tagged & subscribed", text: "New fans land in your list, opted in and tagged." },
];

const createFormStyles = `.sf-create-form:hover, .sf-create-form:focus { color: var(--setlist-primary-fg, #FFFFFF); text-decoration: none; }`;

const emptyStyles = `
.sf-empty-features { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; max-width: 720px; margin: 24px auto 28px; text-align: center; }
.sf-empty-feature { display: flex; flex-direction: column; align-items: center; gap: 4px; }
.sf-empty-feature-icon { display: inline-flex; align-items: center; justify-content: center; width: 40px; height: 40px; margin-bottom: 4px; color: var(--sl-text-quaternary); background: var(--sl-bg-tertiary); border-radius: 9999px; }
.sf-empty-feature-title { font-size: 14px; font-weight: 600; color: var(--sl-text-primary); }
.sf-empty-feature-text { font-size: 14px; line-height: 20px; color: var(--sl-text-tertiary); }
@media (max-width: 640px) { .sf-empty-features { grid-template-columns: 1fr; } }
`;

const SignupFormsIndexContainer = (props) => {
  const { csrfToken, team, venueOwnerships = [], embedBaseUrl, newFormPath, signupForms, submissionsForm: requestedForm } = props;

  const [forms, setForms] = useState(signupForms || []);
  const [search, setSearch] = useState("");
  const [activeFilters, setActiveFilters] = useState({});
  const [page, setPage] = useState(1);
  const [submissionsForm, setSubmissionsForm] = useState(() => {
    const requestedId = Number(new URLSearchParams(window.location.search).get("submissions"));
    return requestedForm || (signupForms || []).find((f) => f.id === requestedId) || null;
  });

  const closeSubmissions = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete("submissions");
    window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);
    setSubmissionsForm(null);
  };

  const filters = useMemo(() => [
    {
      key: "venue",
      label: "Venue",
      type: "search",
      options: venueOwnerships.map((vo) => ({ value: String(vo.id), label: vo.name })),
    },
    { key: "status", label: "Status", type: "checkbox", options: STATUS_OPTIONS },
  ], [venueOwnerships]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const venues = activeFilters.venue || [];
    const statuses = activeFilters.status || [];
    return forms.filter((f) => {
      if (term && !f.name.toLowerCase().includes(term)) return false;
      if (venues.length && !(f.venue_ownerships || []).some((v) => venues.includes(String(v.id)))) return false;
      if (statuses.length && !statuses.includes(f.status)) return false;
      return true;
    });
  }, [forms, search, activeFilters]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const pageRows = filtered.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);

  const editPath = (form) => `/teams/${team.id}/signup_forms/${form.id}/edit`;

  const changeFilters = (next) => { setActiveFilters(next); setPage(1); };

  const archiveForm = async (form) => {
    if (!window.confirm(`Archive "${form.name}"?`)) return;
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      await axios.delete(`/teams/${team.id}/signup_forms/${form.id}`);
      setForms((prev) => prev.filter((f) => f.id !== form.id));
    } catch (error) {
      toastError("Could not archive this form, please try again.");
    }
  };

  const publishForm = async (form) => {
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const res = await axios.post(`/teams/${team.id}/signup_forms/${form.id}/publish`);
      setForms((prev) => prev.map((f) => (f.id === form.id ? { ...f, status: res.data.status } : f)));
      toastSuccess(`"${form.name}" is live and collecting signups.`);
    } catch (error) {
      const body = error.response?.data;
      toastError(Array.isArray(body) ? body[0] : "Could not publish this form, please try again.");
    }
  };

  const unpublishForm = async (form) => {
    if (!window.confirm(`Unpublish "${form.name}"? Its embed will stop collecting signups.`)) return;
    try {
      axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
      const res = await axios.post(`/teams/${team.id}/signup_forms/${form.id}/unpublish`);
      setForms((prev) => prev.map((f) => (f.id === form.id ? { ...f, status: res.data.status } : f)));
      toastSuccess(`"${form.name}" is back to draft and no longer collecting signups.`);
    } catch (error) {
      toastError("Could not unpublish this form, please try again.");
    }
  };

  const onEmbedCopied = (form) => {
    if (form.status === "active") return;
    toastError("Copied — but this form is a draft, so it says it isn't accepting signups until you publish it.");
  };

  const nowrap = (content) => <span className="text-nowrap">{content}</span>;

  const columns = [
    {
      key: "name",
      header: "Form",
      primary: true,
      render: (form) => (
        <a href={editPath(form)} className="d-inline-block text-reset font-weight-bold" style={{ minWidth: 200 }}>{form.name}</a>
      ),
    },
    {
      key: "venues",
      header: "Venue(s)",
      render: (form) => (
        <div className="d-flex flex-wrap" style={{ gap: 6 }}>
          {(form.venue_ownerships || []).map((v) => <Tag key={v.id} size="sm">{v.name}</Tag>)}
        </div>
      ),
    },
    {
      key: "channels",
      header: "Channels",
      render: (form) => (
        <div className="d-flex" style={{ gap: 6 }}>
          {(form.channels || []).map((channel) => (
            <Badge key={channel} size="sm" color={channel === "Email" ? "brand" : "gray"}>{channel}</Badge>
          ))}
        </div>
      ),
    },
    {
      key: "subscribers",
      header: "Subscribers",
      align: "right",
      render: (form) => (
        <span style={{ fontWeight: 600, color: "var(--sl-text-primary)", fontVariantNumeric: "tabular-nums" }}>
          {Number(form.subscriber_count || 0).toLocaleString()}
        </span>
      ),
    },
    { key: "status", header: "Status", render: (form) => nowrap(<FormStatusBadge status={form.status} />) },
    { key: "updated", header: "Last updated", render: (form) => nowrap(form.updated_at_human) },
    {
      key: "actions",
      header: "",
      render: (form) => (
        <div className="d-flex align-items-center justify-content-end" style={{ gap: 8 }}>
          <Button size="sm" onClick={() => setSubmissionsForm(form)}>
            {`Submissions (${Number(form.submission_count || 0).toLocaleString()})`}
          </Button>
          <CopyButton
            size="sm"
            icon={Code02}
            label="Embed"
            copiedLabel="Copied"
            value={() => buildSignupFormEmbed(form, { embedBaseUrl })}
            onCopy={() => onEmbedCopied(form)}
          />
          <Menu
            placement="bottom-end"
            trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${form.name}`} />}
            items={[
              { label: "Edit", icon: Edit05, onClick: () => { window.location.href = editPath(form); } },
              form.status === "active"
                ? { label: "Unpublish", icon: EyeOff, onClick: () => unpublishForm(form) }
                : { label: "Publish", icon: Send01, onClick: () => publishForm(form) },
              { label: "Archive", icon: Trash01, destructive: true, onClick: () => archiveForm(form) },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <ToastContainer icon={false} />
      <style>{createFormStyles}</style>
      <PageHeader
        title="Signup Forms"
        supportingText="Embeddable forms that collect fans and subscribers from your website. Assign each form to one or more venues."
        actions={<Button as="a" href={newFormPath} variant="primary" icon={Plus} className="sf-create-form">Create form</Button>}
      />
      <div className="overflow-hidden" style={cardStyle}>
        {forms.length === 0 ? (
          <div className="px-4 pt-4 pb-5">
            <style>{emptyStyles}</style>
            <EmptyState
              icon={Mail01}
              title="Collect fans straight from your website"
              description="A signup form is an embeddable form you drop onto any website — your homepage, a footer, a landing page. New fans who sign up flow right into your audience, tagged and subscribed to email or SMS, ready for your next campaign."
            />
            <div className="sf-empty-features">
              {EMPTY_FEATURES.map(({ icon: Icon, title, text }) => (
                <div key={title} className="sf-empty-feature">
                  <span className="sf-empty-feature-icon"><Icon width={20} height={20} /></span>
                  <span className="sf-empty-feature-title">{title}</span>
                  <span className="sf-empty-feature-text">{text}</span>
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-center">
              <Button as="a" href={newFormPath} variant="primary" icon={Plus} className="sf-create-form">Create form</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="px-4 py-3">
              <FilterBar
                variant="plain"
                filters={filters}
                values={activeFilters}
                onChange={changeFilters}
                alwaysShow={["venue", "status"]}
                resultCount={`${filtered.length} ${filtered.length === 1 ? "form" : "forms"}`}
                search={(
                  <TextInput
                    size="sm"
                    leadingIcon={SearchLg}
                    value={search}
                    onChange={(event) => { setSearch(event.target.value); setPage(1); }}
                    placeholder="Search forms"
                    aria-label="Search forms"
                  />
                )}
              />
            </div>

            <div className="px-2 py-1">
              <Table
                columns={columns}
                data={pageRows}
                getRowKey={(form) => form.id}
                stackBelow={720}
                stickyLastColumn
                empty={(
                  <EmptyState
                    size="sm"
                    icon={SearchLg}
                    title="No forms found"
                    description="Try a different search or filters."
                  />
                )}
              />
            </div>

            {pageCount > 1 && (
              <div className="px-4 py-3 border-top">
                <Pagination currentPage={currentPage} totalPages={pageCount} onPageChange={setPage} />
              </div>
            )}
          </>
        )}

        <SubmissionsDrawer form={submissionsForm} teamId={team.id} onClose={closeSubmissions} />
      </div>
    </div>
  );
};

export default SignupFormsIndexContainer;
